import * as B from '@blueprintjs/core';
import React from 'react';
import {Link} from 'react-router-dom';

import {
  type NteScope,
  ORDER_LICENSE_BY_SOURCE_ID,
} from 'app/components/OrderImageryModal/orderImageryUtils';
import {
  CartItemGroupCard,
  CartItemRow,
  CartItemV1,
  buyerMessage,
  cartGroupByOptions,
  formatAcresLabel,
  formatPerAcrePrice,
  isInFlight,
  useCollapsedGroups,
} from 'app/modules/Cart';
import type {
  CartCharges,
  CartDisplayGroup,
  CartGroupBy,
  CheckoutFailure,
  PaymentNotice,
} from 'app/modules/Cart';
import type {ApiImageryCheckoutItemError} from 'app/modules/Remote/api';
import {formatCost} from 'app/utils/costUtils';

import CartPreviewLightbox from './CartPreviewLightbox';
import type {UsageLine} from './grouping';
import OrderSummarySidebar from './OrderSummarySidebar';
import PaymentNotices from './PaymentNotices';
import cs from './styles.module.scss';
import type {
  BillingNoteField,
  CartItemPreview,
  FixPlan,
  GroupFailure,
  ItemRejections,
  ItemValidation,
  QuoteChange,
  SubmitBlock,
} from './types';
import UsageLimits from './UsageLimits';
import {rejectionMessage, requotePatch} from './validation';

type GroupFailureBanner = 'fundsRemaining' | 'serverMessage' | 'notSubmitted';

/**
 * Which banner a group-wide failure raises. The cap's message carries no numbers, so it gets a
 * sentence of its own. Checkout copy the server writes for buyers says what happened and what to do
 * next, so it's shown as it is. Everything else shares one banner: a cart spanning contracts and a
 * project with none are races between page load and Submit that a reload resolves, and the rest
 * carry no copy a buyer can act on. Keyed over every kind so a new one fails the build instead of
 * shipping invisible.
 */
const GROUP_FAILURE_BANNERS: Record<
  CheckoutFailure<CartItemV1>['kind'],
  GroupFailureBanner | null
> = {
  itemRejections: null,
  nteExceeded: 'fundsRemaining',
  mixedContracts: 'notSubmitted',
  noContract: 'notSubmitted',
  badRequest: 'serverMessage',
  unknown: 'serverMessage',
};

const bannerFor = (failure: CheckoutFailure<CartItemV1>): GroupFailureBanner | null => {
  const banner = GROUP_FAILURE_BANNERS[failure.kind];
  return banner === 'serverMessage' && buyerMessage(failure) === null ? 'notSubmitted' : banner;
};

const GroupFailureBanners: React.FC<{
  failures: GroupFailure[];
  contractGroupCount: number;
  nteScope: NteScope;
}> = ({failures, contractGroupCount, nteScope}) => {
  const banners = new Set(failures.map(({failure}) => bannerFor(failure)));
  // Each message once, however many groups it came back for.
  const serverFailures = [
    ...new Map(
      failures.flatMap(({failure}) =>
        failure.kind !== 'itemRejections' && bannerFor(failure) === 'serverMessage'
          ? [[failure.message, failure] as const]
          : []
      )
    ).values(),
  ];
  return (
    <>
      {banners.has('fundsRemaining') && (
        <B.Callout intent={B.Intent.DANGER} icon="error" title="Not enough funds remaining">
          {/* One banner covers every capped group, so it can only name a portfolio when the cart
              bills to just one. */}
          These scenes cost more than the funds remaining for{' '}
          {nteScope === 'organization'
            ? 'your organization'
            : contractGroupCount > 1
              ? 'one or more portfolios'
              : 'this portfolio'}
          . Please have an account administrator contact us for options.
        </B.Callout>
      )}
      {serverFailures.map((failure) => {
        // A sibling request still holds the key and finishes on its own; nothing has failed yet.
        const inFlight = isInFlight(failure);
        return (
          <B.Callout
            key={failure.message}
            intent={inFlight ? B.Intent.WARNING : B.Intent.DANGER}
            icon={inFlight ? 'time' : 'error'}
            title={inFlight ? 'We couldn’t finish your checkout' : 'We couldn’t submit your order'}
          >
            {failure.message}
          </B.Callout>
        );
      })}
      {banners.has('notSubmitted') && (
        <B.Callout intent={B.Intent.DANGER} icon="error" title="We couldn’t submit your order">
          Reload the page and try again. If it keeps happening, contact support.
        </B.Callout>
      )}
    </>
  );
};

const describeChange = (change: QuoteChange): string =>
  change.kind === 'price'
    ? `${formatPerAcrePrice(change.from)} → ${formatPerAcrePrice(change.to)}`
    : `${formatAcresLabel(change.from)} → ${formatAcresLabel(change.to)}`;

const rejectionActionLabel = (error: ApiImageryCheckoutItemError): string =>
  error.code === 'priceChanged'
    ? 'Accept new price'
    : error.code === 'acresMismatch'
      ? 'Accept new acreage'
      : 'Remove';

const RowStatus: React.FC<{
  item: CartItemV1;
  validation: ItemValidation | undefined;
  rejection: ApiImageryCheckoutItemError | undefined;
  onRemoveBlocked: (item: CartItemV1) => void;
  onAcceptRequote: (item: CartItemV1) => void;
}> = ({item, validation, rejection, onRemoveBlocked, onAcceptRequote}): React.ReactElement => {
  if (rejection) {
    const canRequote = requotePatch(rejection) !== null;
    return (
      <div className={cs.rowStatusBlocked}>
        <B.Icon icon="warning-sign" size={12} />
        <span>{rejectionMessage(rejection, item)}</span>
        <B.Button
          small
          intent={canRequote ? B.Intent.PRIMARY : B.Intent.DANGER}
          onClick={() => (canRequote ? onAcceptRequote(item) : onRemoveBlocked(item))}
        >
          {rejectionActionLabel(rejection)}
        </B.Button>
      </div>
    );
  }

  switch (validation?.status.kind) {
    case undefined:
    case 'validating':
      return (
        <div className={cs.rowStatusMuted}>
          <B.Spinner size={12} />
          <span>Checking price and availability…</span>
        </div>
      );
    case 'updated':
      return (
        <div className={cs.rowStatusUpdated}>
          <B.Tag minimal intent={B.Intent.WARNING}>
            Updated
          </B.Tag>
          <span>{validation.status.changes.map(describeChange).join(' · ')}</span>
        </div>
      );
    case 'unorderable':
      return (
        <div className={cs.rowStatusBlocked}>
          <B.Icon icon="warning-sign" size={12} />
          <span>{validation.status.reason}</span>
          <B.Button small intent={B.Intent.DANGER} onClick={() => onRemoveBlocked(item)}>
            Remove
          </B.Button>
        </div>
      );
    case 'ok':
      return <></>;
  }
};

const FixBanner: React.FC<{plan: FixPlan; onApplyAllFixes: () => void}> = ({
  plan,
  onApplyAllFixes,
}) => {
  const parts: string[] = [];
  if (plan.removeItemIds.length > 0) {
    parts.push(
      `${plan.removeItemIds.length} ${plan.removeItemIds.length === 1 ? 'scene' : 'scenes'} can’t be ordered`
    );
  }
  if (plan.requotes.length > 0) {
    parts.push(
      `${plan.requotes.length} ${plan.requotes.length === 1 ? 'scene has' : 'scenes have'} a new quote`
    );
  }

  return (
    <B.Callout intent={B.Intent.WARNING} icon="wrench" title="Some scenes need attention">
      <p>
        {parts.join(' and ')}. Applying all fixes drops what can’t be ordered and accepts every new
        quote.
      </p>
      <B.Button intent={B.Intent.WARNING} onClick={onApplyAllFixes}>
        Apply all fixes
      </B.Button>
    </B.Callout>
  );
};

export interface CartPageViewProps {
  displayGroups: CartDisplayGroup[];
  groupBy: CartGroupBy;
  /** False hides the Property option: nothing in the cart shares a feature, so it would group nothing. */
  propertyGroupingIsAvailable: boolean;
  onGroupByChange: (groupBy: CartGroupBy) => void;
  validations: ReadonlyMap<string, ItemValidation>;
  /** Rows whose feature and scene the refresh pass has; the rest preview from their snapshot. */
  previews: ReadonlyMap<string, CartItemPreview>;
  rejections: ItemRejections;
  /** Failures the rows can't explain: they name a contract group, not a scene. */
  groupFailures: GroupFailure[];
  /** How many contracts the cart bills to, for whether a failure banner can say "this portfolio". */
  contractGroupCount: number;
  /** Whose funds a cap failure names: an organization budget's, or each contract's. */
  nteScope: NteScope;
  unmatchedErrors: ApiImageryCheckoutItemError[];
  /** What earlier submits in this session already ordered, if the cart still has items left. */
  orderedSummary: {itemCount: number; totalCents: number} | null;
  fixPlan: FixPlan;
  itemCount: number;
  totalCents: number;
  totalAcres: number;
  isSubmitting: boolean;
  /** Non-null disables Submit and says why. */
  submitBlock: SubmitBlock | null;
  /** The cart's card picker, when the cart charges at checkout. */
  paymentSection: React.ReactNode;
  /** The note for a card checkout's invoice; null when the cart bills a contract. */
  billingNote: BillingNoteField | null;
  isPayNow: boolean;
  /** Prepaid credit covers the whole order, so Submit charges no card. */
  paysWithCredit: boolean;
  charges: CartCharges | null;
  /** The spending limits and prepaids the cart draws on, as they stand before this order. */
  usage: UsageLine[];
  /** Why a charge didn't go through, on rows that are therefore still in the cart. */
  paymentNotices: PaymentNotice[];
  browsePropertiesUrl: string;
  deepLinkFor: (item: CartItemV1) => string | undefined;
  /** Link to an item's property on the map, for the property view's group titles. */
  propertyLinkFor: (item: CartItemV1) => string;
  /** The row's own × button, wired to removeWithUndo so clicking it can be taken back. */
  onRemove: (item: CartItemV1) => void;
  /** Removal of a row that can't be ordered — deliberate, so not undoable like onRemove. */
  onRemoveBlocked: (item: CartItemV1) => void;
  onAcceptRequote: (item: CartItemV1) => void;
  onApplyAllFixes: () => void;
  onClearCart: () => void;
  onSubmit: () => void;
}

/** The checkout page with every server call already resolved into props. */
export const CartPageView: React.FC<CartPageViewProps> = ({
  displayGroups,
  groupBy,
  propertyGroupingIsAvailable,
  onGroupByChange,
  validations,
  previews,
  rejections,
  groupFailures,
  contractGroupCount,
  nteScope,
  unmatchedErrors,
  orderedSummary,
  fixPlan,
  itemCount,
  totalCents,
  totalAcres,
  isSubmitting,
  submitBlock,
  paymentSection,
  billingNote,
  isPayNow,
  paysWithCredit,
  charges,
  usage,
  paymentNotices,
  browsePropertiesUrl,
  deepLinkFor,
  propertyLinkFor,
  onRemove,
  onRemoveBlocked,
  onAcceptRequote,
  onApplyAllFixes,
  onClearCart,
  onSubmit,
}) => {
  const {isCollapsed, toggle} = useCollapsedGroups(groupBy);
  const [isConfirmingClear, setIsConfirmingClear] = React.useState(false);
  const [arePreviewsExpanded, setArePreviewsExpanded] = React.useState(false);
  const [previewIndex, setPreviewIndex] = React.useState<number | null>(null);

  // The empty-cart view unmounts the lightbox without closing it, so a stale index would reopen
  // it the moment an undo brings an item back.
  React.useEffect(() => {
    if (displayGroups.length === 0) {
      setPreviewIndex(null);
    }
  }, [displayGroups.length]);

  // The lightbox steps in the order the page shows, collapsed cards included.
  const orderedItems = React.useMemo(
    () => displayGroups.flatMap((group) => group.items),
    [displayGroups]
  );
  // Sources that share a provider share its license, so each one is listed once.
  const licenses = React.useMemo(
    () => [
      ...new Set(orderedItems.flatMap((item) => ORDER_LICENSE_BY_SOURCE_ID[item.sourceId] ?? [])),
    ],
    [orderedItems]
  );
  const openPreview = (item: CartItemV1) =>
    setPreviewIndex(orderedItems.findIndex((candidate) => candidate.id === item.id));

  const hasFixes = fixPlan.removeItemIds.length + fixPlan.requotes.length > 0;

  // A re-quoted row (frontend and backend disagree on price, user hit "requote") is deliberately left out:
  // it auto-accepts and never blocks submit.
  const needsAttention = (item: CartItemV1): boolean =>
    rejections.has(item.id) || validations.get(item.id)?.status.kind === 'unorderable';

  if (displayGroups.length === 0) {
    return (
      <div className={cs.cartPage}>
        <B.NonIdealState
          icon="shopping-cart"
          title="Your cart is empty"
          description="Add scenes from a property to review and order them here"
          action={
            <Link to={browsePropertiesUrl}>
              <B.Button intent={B.Intent.PRIMARY}>Browse properties</B.Button>
            </Link>
          }
        />
      </div>
    );
  }

  return (
    <div className={cs.cartPage}>
      <header className={cs.pageHeader}>
        <h2>Your cart</h2>
      </header>

      {orderedSummary && (
        <B.Callout
          intent={B.Intent.SUCCESS}
          icon="tick-circle"
          title="Part of your order went through"
        >
          <p>
            {orderedSummary.itemCount}{' '}
            {orderedSummary.itemCount === 1 ? 'scene was' : 'scenes were'} ordered for{' '}
            <strong>{formatCost(orderedSummary.totalCents)}</strong> and removed from your cart. The
            scenes below weren’t ordered.
          </p>
        </B.Callout>
      )}

      <PaymentNotices notices={paymentNotices} />

      <GroupFailureBanners
        failures={groupFailures}
        contractGroupCount={contractGroupCount}
        nteScope={nteScope}
      />

      {hasFixes && <FixBanner plan={fixPlan} onApplyAllFixes={onApplyAllFixes} />}

      {unmatchedErrors.length > 0 && (
        // The server only ever rejects scenes it was sent, so an error naming a scene nobody has a
        // row for is a fault on our side, not something the customer can fix.
        <B.Callout intent={B.Intent.DANGER} icon="error" title="Something went wrong">
          The server reported a problem with scenes that aren’t in your cart. Reload the page and
          try again.
        </B.Callout>
      )}

      <div className={cs.layout}>
        <div className={cs.groups}>
          <div className={cs.groupControls}>
            <B.SegmentedControl
              value={groupBy}
              onValueChange={(value) => onGroupByChange(value as CartGroupBy)}
              options={cartGroupByOptions(propertyGroupingIsAvailable)}
            />
            <div className={cs.groupActions}>
              <B.Button
                minimal
                icon={arePreviewsExpanded ? 'minimize' : 'maximize'}
                onClick={() => setArePreviewsExpanded((expanded) => !expanded)}
              >
                {arePreviewsExpanded ? 'Shrink previews' : 'Expand previews'}
              </B.Button>
              <B.Button
                minimal
                intent={B.Intent.DANGER}
                disabled={isSubmitting}
                onClick={() => setIsConfirmingClear(true)}
              >
                Clear cart
              </B.Button>
            </div>
          </div>

          {displayGroups.map((group) => (
            <CartItemGroupCard
              key={group.key}
              group={group}
              isCollapsed={isCollapsed(group)}
              onToggle={() => toggle(group)}
              titleUrl={groupBy === 'property' ? propertyLinkFor(group.items[0]) : undefined}
              needsAttentionCount={group.items.filter(needsAttention).length}
            >
              {group.items.map((item) => (
                <CartItemRow
                  key={item.id}
                  item={item}
                  layout="checkout"
                  groupBy={groupBy}
                  deepLinkUrl={deepLinkFor(item)}
                  onRemove={onRemove}
                  onPreview={openPreview}
                  isThumbnailLarge={arePreviewsExpanded}
                  isBusy={isSubmitting}
                >
                  {/* Kept mounted so screen readers announce a later status change. */}
                  <div role="status">
                    <RowStatus
                      item={item}
                      validation={validations.get(item.id)}
                      rejection={rejections.get(item.id)}
                      onRemoveBlocked={onRemoveBlocked}
                      onAcceptRequote={onAcceptRequote}
                    />
                  </div>
                </CartItemRow>
              ))}
            </CartItemGroupCard>
          ))}
        </div>

        <div className={cs.sidebar}>
          {usage.length > 0 && <UsageLimits lines={usage} />}
          <div className={cs.summary}>
            <OrderSummarySidebar
              itemCount={itemCount}
              totalCents={totalCents}
              totalAcres={totalAcres}
              isSubmitting={isSubmitting}
              submitBlock={submitBlock}
              paymentSection={paymentSection}
              billingNote={billingNote}
              isPayNow={isPayNow}
              paysWithCredit={paysWithCredit}
              charges={charges}
              licenses={licenses}
              onSubmit={onSubmit}
            />
          </div>
        </div>
      </div>

      <CartPreviewLightbox
        items={orderedItems}
        previews={previews}
        index={previewIndex}
        onIndexChange={setPreviewIndex}
        onClose={() => setPreviewIndex(null)}
        onRemove={onRemove}
        isBusy={isSubmitting}
      />

      <B.Alert
        isOpen={isConfirmingClear}
        cancelButtonText="Cancel"
        confirmButtonText="Clear cart"
        intent={B.Intent.DANGER}
        canOutsideClickCancel
        canEscapeKeyCancel
        icon="trash"
        onCancel={() => setIsConfirmingClear(false)}
        onConfirm={() => {
          onClearCart();
          setIsConfirmingClear(false);
        }}
      >
        This removes all {itemCount} {itemCount === 1 ? 'scene' : 'scenes'} from your cart.
      </B.Alert>
    </div>
  );
};

export default CartPageView;
